import { Target, TrendingUp, Cog, FlaskConical, Database, Wrench, Expand, Shield, Eye, MonitorCog, UserCheck, Users } from 'lucide-react';

export interface EvaluationComponent {
  id: string;
  dimension: string;
  dimensionLetter: string;
  dimensionName: string;
  name: string;
  shortName: string;
  description: string;
  level5: string;
  question: string;
  placeholder: string;
  helpText?: string;
  example?: string;
  weight: number; // Default weight percentage
  iconName: string;
  categoryColor: string;
}

export const LIDER_IA_COMPONENTS: EvaluationComponent[] = [
  {
    id: 'strategic_alignment',
    dimension: 'L',
    dimensionLetter: 'L',
    dimensionName: 'Liderazgo y Valor',
    name: 'Alineación Estratégica',
    shortName: 'Alineación',
    description: 'Evalúa si el proyecto impacta directamente los objetivos del "Norte" corporativo.',
    level5: 'El proyecto es pilar fundamental del Plan Estratégico vigente.',
    question: '¿A qué objetivo clave de la empresa contribuye este proyecto y qué problema crítico de la visión corporativa resuelve de forma específica?',
    placeholder: 'Describe cómo tu proyecto se alinea con los objetivos estratégicos de la organización y qué problema crítico resuelve...',
    helpText: 'Queremos saber para qué le sirve tu proyecto a la empresa. No necesitas cifras: basta con nombrar el objetivo del negocio (vender más, reducir costos, mejorar el servicio) y el problema concreto que atacas.',
    example: 'Nuestro objetivo anual es reducir el tiempo de respuesta al cliente. El proyecto responde las preguntas frecuentes de soporte en minutos, que hoy toman hasta 2 días.',
    weight: 10,
    iconName: 'Target',
    categoryColor: 'bg-blue-100 text-blue-600',
  },
  {
    id: 'expected_roi',
    dimension: 'L',
    dimensionLetter: 'L',
    dimensionName: 'Liderazgo y Valor',
    name: 'Impacto esperado / ROI',
    shortName: 'ROI',
    description: 'Mide la capacidad de generar retorno financiero, ahorro de costos o eficiencia operativa validada.',
    level5: 'Business Case sólido con ROI proyectado, medible y auditado.',
    question: '¿Cuál es el retorno a la inversión esperado (ahorros, horas hombre, etc.), cuáles fueron los costos para construir la solución y cuáles son los costos proyectados para mantener esta solución en el tiempo?',
    placeholder: 'Describe el ROI esperado, costos de construcción y costos proyectados de mantenimiento...',
    helpText: 'Explica qué ganas con la solución (tiempo, dinero, errores evitados) y cuánto costó hacerla y mantenerla. Estimaciones aproximadas son válidas; si no las calculaste, dilo y explica por qué.',
    example: 'Estimamos ahorrar 20 horas al mes del equipo (~$2.000.000 al año). Construirlo tomó 30 horas de trabajo interno y el mantenimiento sería la suscripción de la herramienta (~$300.000 al año).',
    weight: 12,
    iconName: 'TrendingUp',
    categoryColor: 'bg-blue-100 text-blue-600',
  },
  {
    id: 'process_optimization',
    dimension: 'I',
    dimensionLetter: 'I',
    dimensionName: 'Innovación y Procesos',
    name: 'Optimización de Procesos',
    shortName: 'Procesos',
    description: 'Evalúa si el proceso fue rediseñado o simplificado antes de aplicar la IA (No automatizar el caos).',
    level5: 'Proceso optimizado mediante Lean/Process Mining antes de la implementación.',
    question: '¿Qué análisis o rediseño se realizó sobre el proceso original antes de implementar la IA para asegurar que no se está automatizando una ineficiencia preexistente?',
    placeholder: 'Describe el análisis o rediseño del proceso original previo a la implementación de IA...',
    helpText: 'Antes de automatizar con IA, ¿revisaste el proceso actual para ver si tenía pasos innecesarios? Se trata de no automatizar el desorden. Si no lo revisaste, decláralo con sinceridad.',
    example: 'Mapeamos el proceso actual de 7 pasos y eliminamos 2 aprobaciones duplicadas antes de automatizar el resto con IA.',
    weight: 8,
    iconName: 'Cog',
    categoryColor: 'bg-purple-100 text-purple-600',
  },
  {
    id: 'prototype_maturity',
    dimension: 'I',
    dimensionLetter: 'I',
    dimensionName: 'Innovación y Procesos',
    name: 'Madurez del Prototipo',
    shortName: 'Prototipo',
    description: 'Mide el estado de desarrollo y los resultados obtenidos en fases de experimentación (Sandbox).',
    level5: 'Prototipo validado con métricas de precisión y confianza en entornos controlados.',
    question: '¿Qué grado de avance real lograste en esta fase, cuáles fueron los resultados cuantitativos y si fue necesario utilizar datos de ejemplo/ficticios para las pruebas?',
    placeholder: 'Indica la etapa actual, resultados cuantitativos y si utilizaste datos ficticios...',
    helpText: 'Cuenta hasta dónde llegaste realmente: ¿es una idea, una demo, algo probado con usuarios? Y si usaste datos ficticios o de ejemplo para probar, dilo, es totalmente válido en un prototipo.',
    example: 'Llegamos a una demo funcional probada con 15 casos reales; acertó en 12 de 15. Para 3 pruebas usamos datos ficticios porque no teníamos acceso a la base real.',
    weight: 8,
    iconName: 'FlaskConical',
    categoryColor: 'bg-purple-100 text-purple-600',
  },
  {
    id: 'data_quality',
    dimension: 'D',
    dimensionLetter: 'D',
    dimensionName: 'Datos y Tecnología',
    name: 'Disponibilidad y calidad de datos',
    shortName: 'Datos',
    description: 'Analiza la existencia, limpieza, accesibilidad y linaje de los datos necesarios.',
    level5: 'Datos de alta calidad, disponibles en tiempo real y con gobierno de datos activo.',
    question: '¿Los datos que necesitabas para el modelo fueron accesibles fácilmente? Explica las fuentes utilizadas y cómo garantizaste su limpieza y trazabilidad.',
    placeholder: 'Describe las fuentes de datos, accesibilidad, limpieza y trazabilidad...',
    helpText: '¿De dónde salen los datos que usa tu solución, fue fácil conseguirlos y qué tan confiables/limpios están? Si trabajaste sin datos reales o no revisaste su calidad, indícalo.',
    example: 'Usamos un Excel del área comercial actualizado mensualmente. Tuvimos que limpiar duplicados a mano. No existe un proceso formal de gobierno de datos todavía.',
    weight: 12,
    iconName: 'Database',
    categoryColor: 'bg-amber-100 text-amber-600',
  },
  {
    id: 'feasibility_complexity',
    dimension: 'D',
    dimensionLetter: 'D',
    dimensionName: 'Datos y Tecnología',
    name: 'Factibilidad y Complejidad',
    shortName: 'Factibilidad',
    description: 'Evalúa el esfuerzo técnico versus la capacidad de la arquitectura actual para soportar la solución.',
    level5: 'Integración fluida mediante APIs, baja deuda técnica y arquitectura moderna.',
    question: 'Del 1 al 5, ¿qué tan complejo fue crear este prototipo? ¿Pudiste implementar la solución de forma autónoma o requeriste ayuda técnica especializada para la integración?',
    placeholder: 'Indica la complejidad (1-5) y si necesitaste apoyo técnico especializado...',
    helpText: 'Cuéntanos qué tan difícil fue construirlo (1 = muy fácil, 5 = muy complejo) y si lo hiciste solo o necesitaste apoyo técnico. No hay respuesta \'correcta\'.',
    example: 'Complejidad 2 de 5. Lo hice de forma autónoma con herramientas no-code; solo pedí ayuda de sistemas para conectar el correo corporativo.',
    weight: 8,
    iconName: 'Wrench',
    categoryColor: 'bg-amber-100 text-amber-600',
  },
  {
    id: 'scalability_reuse',
    dimension: 'D',
    dimensionLetter: 'D',
    dimensionName: 'Datos y Tecnología',
    name: 'Escalabilidad y reutilización',
    shortName: 'Escalabilidad',
    description: 'Mide si la solución puede crecer en volumen o si sus componentes sirven para otros proyectos.',
    level5: 'Diseño modular y elástico, fácilmente replicable en otras áreas de la empresa.',
    question: '¿Puede esta solución ampliarse a otros procesos o áreas geográficas de la compañía y qué componentes técnicos pueden ser reutilizados en futuros desarrollos?',
    placeholder: 'Explica el potencial de escalabilidad y reutilización de componentes...',
    helpText: '¿Esta solución podría usarse en otras áreas, sedes o con más volumen? ¿Algo de lo que hiciste (prompts, plantillas, flujos) sirve para otros proyectos? Si no lo pensaste, puedes decirlo.',
    example: 'El mismo flujo podría aplicarse en Recursos Humanos cambiando la base de conocimiento. Los prompts y la plantilla de respuestas son reutilizables.',
    weight: 7,
    iconName: 'Expand',
    categoryColor: 'bg-amber-100 text-amber-600',
  },
  {
    id: 'governance_ethics_security',
    dimension: 'E',
    dimensionLetter: 'E',
    dimensionName: 'Ética y Seguridad',
    name: 'Gobernanza & Ética + Riesgos y Seguridad',
    shortName: 'Gobernanza',
    description: 'Evalúa el marco normativo, cumplimiento legal (SIC/Ley 1581) y defensa ante ciberataques de IA.',
    level5: 'Cumple con ISO 42001, tiene filtros activos (Guardrails) y Red Teaming periódico.',
    question: '¿Cómo definiste los controles de seguridad, mitigación de sesgos y cumplimiento ético/legal (ej. Ley 1581) para evitar riesgos reputacionales o técnicos?',
    placeholder: 'Describe controles de seguridad, mitigación de sesgos y cumplimiento normativo...',
    helpText: '¿Pensaste en la privacidad de los datos, en posibles sesgos o en el cumplimiento legal (por ejemplo Ley 1581 de datos personales)? Si no aplicaste controles, es válido decirlo y explicar por qué.',
    example: 'No incluimos datos personales de clientes; solo información pública de productos. No definimos controles formales de sesgo porque el prototipo no toma decisiones sobre personas.',
    weight: 12,
    iconName: 'Shield',
    categoryColor: 'bg-emerald-100 text-emerald-600',
  },
  {
    id: 'explainability',
    dimension: 'E',
    dimensionLetter: 'E',
    dimensionName: 'Ética y Seguridad',
    name: 'Explicabilidad (XAI)',
    shortName: 'XAI',
    description: 'Mide la capacidad del modelo para justificar sus decisiones de forma comprensible para humanos.',
    level5: 'Trazabilidad total de decisiones disponible para usuarios finales y auditores.',
    question: '¿Cómo se asegura que las respuestas del modelo sean interpretables para el usuario y qué lógica se sigue para auditar un resultado inesperado o erróneo?',
    placeholder: 'Describe cómo garantizas la interpretabilidad y auditabilidad del modelo...',
    helpText: '¿El usuario entiende por qué la IA respondió lo que respondió? ¿Se puede revisar de dónde salió una respuesta equivocada? Si no lo trabajaste, dilo con honestidad.',
    example: 'La herramienta cita el documento y la página de donde toma la respuesta, así el usuario puede verificarla. No tenemos aún un proceso formal de auditoría de errores.',
    weight: 5,
    iconName: 'Eye',
    categoryColor: 'bg-emerald-100 text-emerald-600',
  },
  {
    id: 'mlops_monitoring',
    dimension: 'D',
    dimensionLetter: 'D',
    dimensionName: 'Datos y Tecnología',
    name: 'MLOps, Monitorización + Sostenibilidad',
    shortName: 'MLOps',
    description: 'Evalúa el ciclo de vida técnico: despliegue automático, monitoreo de deriva (drift) y mantenimiento.',
    level5: 'Pipeline de CI/CD completo con alertas automáticas y soporte operacional definido.',
    question: '¿Tienes definido el proceso de actualizaciones y mantenimiento técnico? ¿Implementaste MLOps y/o Monitorización de la solución?',
    placeholder: 'Describe el proceso de mantenimiento, MLOps y monitorización implementado...',
    helpText: '¿Definiste cómo se va a mantener y vigilar la solución en el tiempo (quién la actualiza, cómo se detecta si empieza a fallar)? Muy común que en un prototipo aún no exista: decláralo.',
    example: 'Aún no implementamos monitoreo automático. El plan es revisar manualmente una muestra de respuestas cada semana y actualizar la base de conocimiento mensualmente.',
    weight: 10,
    iconName: 'MonitorCog',
    categoryColor: 'bg-amber-100 text-amber-600',
  },
  {
    id: 'algorithmic_responsibility',
    dimension: 'R',
    dimensionLetter: 'R',
    dimensionName: 'Roles y Talento',
    name: 'Responsabilidad Algorítmica y Supervisión Humana',
    shortName: 'Supervisión',
    description: 'Define quién responde por el resultado y cómo interviene un humano en decisiones críticas.',
    level5: 'Protocolo "Human-in-the-loop" robusto con responsables de negocio claramente asignados.',
    question: '¿Quién es el dueño de negocio que asume la responsabilidad del resultado y qué protocolos de supervisión (human-in-the-loop) se han establecido?',
    placeholder: 'Indica el responsable de negocio y los protocolos de supervisión humana...',
    helpText: '¿Quién responde por lo que la solución produce y en qué momento revisa un humano antes de actuar? Si todavía no hay un dueño definido, dilo.',
    example: 'La jefa de Servicio al Cliente es la dueña del proceso. Toda respuesta generada se revisa por un agente antes de enviarse al cliente (human-in-the-loop).',
    weight: 4,
    iconName: 'UserCheck',
    categoryColor: 'bg-rose-100 text-rose-600',
  },
  {
    id: 'adoption_change',
    dimension: 'R',
    dimensionLetter: 'R',
    dimensionName: 'Roles y Talento',
    name: 'Adopción y gestión del cambio',
    shortName: 'Adopción',
    description: 'Mide la estrategia para que los usuarios finales confíen y utilicen la herramienta efectivamente.',
    level5: 'Plan de alfabetización (AI Literacy) ejecutado y alta tasa de uso por parte del negocio.',
    question: 'Cuéntanos sobre la aceptación por parte de los usuarios finales: ¿Qué plan de capacitación se ejecutó y cómo garantizas que la herramienta sea adoptada en el flujo de trabajo diario?',
    placeholder: 'Describe el plan de capacitación, aceptación de usuarios y adopción en flujo de trabajo...',
    helpText: '¿Los usuarios finales la usarían? ¿Hiciste capacitación, socialización o pruebas con ellos? Si aún no la has mostrado a nadie, es válido indicarlo.',
    example: 'Hicimos una demo con 5 agentes; 4 dijeron que la usarían a diario. Falta la capacitación formal y definir que quede dentro de su flujo de trabajo actual.',
    weight: 4,
    iconName: 'Users',
    categoryColor: 'bg-rose-100 text-rose-600',
  },
];

export const DIMENSION_COLORS: Record<string, string> = {
  L: 'bg-blue-100 text-blue-700',
  I: 'bg-purple-100 text-purple-700',
  D: 'bg-amber-100 text-amber-700',
  E: 'bg-emerald-100 text-emerald-700',
  R: 'bg-rose-100 text-rose-700',
};

export const DIMENSION_NAMES: Record<string, string> = {
  L: 'Liderazgo y Valor',
  I: 'Innovación y Procesos',
  D: 'Datos y Tecnología',
  E: 'Ética y Seguridad',
  R: 'Roles y Talento',
};

export const CLASSIFICATION_MATRIX = [
  { range: '400 - 500', classification: 'Quick Win', action: 'Escalar en ≤ 3 meses. Proyecto maduro con alto retorno y bajo riesgo.', color: 'bg-success/10 text-success border-success/30' },
  { range: '300 - 399', classification: 'Strategic Play', action: 'Piloto ampliado (3-6 meses). Necesita ajustes menores antes de escala masiva.', color: 'bg-info/10 text-info border-info/30' },
  { range: '200 - 299', classification: 'Experiment', action: 'Mantener en Sandbox. Refinar hipótesis y mejorar calidad de datos/procesos.', color: 'bg-warning/10 text-warning border-warning/30' },
  { range: '< 200', classification: 'Back-log', action: 'Archivar o replantear. El riesgo es muy alto o el valor estratégico insuficiente.', color: 'bg-destructive/10 text-destructive border-destructive/30' },
];
